import { Stack, Tooltip, Typography, TypographyVariant } from '@mui/material';
import { UnitTooltip } from './UnitTooltip';
import { useProductsTFunction } from '@retail/products/i18n';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '@retail/products/utils';

interface Props {
  price: string | number;
  unit?: string;
  description?: string;
  PriceTooltipContent?: ReactNode;
  typographyVariant?: TypographyVariant;
  lineThrough?: boolean;
}

export const PricePerUnit = ({
  unit,
  price,
  description,
  PriceTooltipContent,
  typographyVariant = 'subtitle2',
  lineThrough,
}: Props) => {
  const t = useProductsTFunction();
  const {
    i18n: { language },
  } = useTranslation();
  const effectiveLangague = language === 'se' ? 'sv' : language;
  const localeFormattedPrice =
    typeof price === 'number' ? formatPrice(price, effectiveLangague) : price;

  const Price = PriceTooltipContent ? (
    <Tooltip title={PriceTooltipContent}>
      <Typography variant={typographyVariant}>
        {t('products.prices.pricePer', { price: localeFormattedPrice })}
      </Typography>
    </Tooltip>
  ) : (
    <Typography variant={typographyVariant}>
      {t('products.prices.pricePer', { price: localeFormattedPrice })}
    </Typography>
  );

  return (
    <Stack
      flexDirection="row"
      gap={0.4}
      alignItems="center"
      sx={
        lineThrough
          ? {
              textDecoration: 'line-through',
              textDecorationColor: (theme) => theme.vars.palette.text.disabled,
            }
          : undefined
      }
    >
      {Price}
      <UnitTooltip
        unit={unit ?? '-'}
        description={description}
        typographyProps={{ variant: typographyVariant }}
      />
    </Stack>
  );
};
